import { exportWithSchema } from './excelExporter';
import {
  buildTotalBar,
  buildFilename,
  pdfStylesBase,
  pdfDefaultStyle,
  formatDate,
  formatDateTime,
} from './pdfVehiculosCore';
import { getExportTimestamp } from '../formatters/dates';

const pdfMake = require('pdfmake/build/pdfmake.js');
const pdfFonts = require('pdfmake/build/vfs_fonts.js');
pdfMake.vfs = pdfFonts.pdfMake.vfs;

const SALES_SCHEMA = {
  vin: { header: 'VIN', value: (r) => r.VIN || '-' },
  situacion: { header: 'Situación', value: (r) => r.Situacion || '-' },
  marca: { header: 'Marca', value: (r) => r.Marca || '-' },
  familia: { header: 'Familia', value: (r) => r.Familia || r.Modelo || '-' },
  catalogo: { header: 'Catálogo', value: (r) => r.Catalogo || r.Version || '-' },
  anoModelo: { header: 'Año', value: (r) => r.Año || r.AnoModelo || '-' },
  equipamiento: { header: 'Equipamiento', value: (r) => r.Equipamiento || '-' },
  descripcion: { header: 'Descripción', value: (r) => r.Descripcion || '-' },
  colorExt: { header: 'Color Ext.', value: (r) => r.ColorExterior || r.Color || '-' },
  colorInt: { header: 'Color Int.', value: (r) => r.ColorInterior || r.ColorInt || '-' },
  sitio: { header: 'Sitio', value: (r) => r.sitio || '-' },
  subSitio: { header: 'Subsitio', value: (r) => r.subSitio || '-' },
  diasAntiguedad: { header: 'Días inv.', value: (r) => r.diasAntiguedad ?? '-' },
  fecha: { header: 'Fecha Recibo', type: 'date', value: (r) => r.FechaRecibo || r.fecharecibo || null },
  observaciones: { header: 'Observaciones', value: (r) => r.Observaciones || '-' },
};

export async function exportSalesDashboardExcel(rows, customColumns) {
  if (!rows?.length) return;

  const visibleColumns = customColumns?.length
    ? customColumns.map((c) => c.id)
    : Object.keys(SALES_SCHEMA);

  await exportWithSchema({
    rows,
    visibleColumns,
    schema: SALES_SCHEMA,
    filename: `inventario_ventas_${getExportTimestamp()}`,
    sheetName: 'Inventario',
    title: 'Inventario Disponible para Venta',
  });
}

export function exportSalesDashboardPDF(rows, customColumns) {
  if (!rows?.length) return;

  const visibleColumns = customColumns?.length
    ? customColumns.map((c) => c.id)
    : Object.keys(SALES_SCHEMA);

  const headerLabels = {};
  visibleColumns.forEach((key) => {
    headerLabels[key] = SALES_SCHEMA[key]?.header ?? key;
  });

  const colCount = visibleColumns.length;
  let finalFontSize = 6;
  let pageSize = 'A4';
  if (colCount > 10) finalFontSize = 5;
  if (colCount > 12) { pageSize = 'LEGAL'; finalFontSize = 5; }
  if (colCount > 16) finalFontSize = 4;

  const getSafeColumnWidth = (key) => {
    switch (key) {
      case 'anoModelo': return '4%';
      case 'diasAntiguedad': return '4%';
      case 'colorExt':
      case 'colorInt': return '6%';
      case 'fecha': return '7%';
      case 'vin': return '10%';
      default: return '*';
    }
  };

  const headerRow = visibleColumns.map((key) => ({
    text: headerLabels[key],
    style: 'tableHeader',
    fillColor: '#111827',
    color: '#FFFFFF',
    margin: [4, 6, 4, 6],
    fontSize: finalFontSize + 1,
  }));

  const bodyRows = rows.map((row) =>
    visibleColumns.map((key) => {
      const col = SALES_SCHEMA[key] || {};
      let value = typeof col.value === 'function' ? col.value(row) : row[key];
      if (key === 'fecha') value = formatDate(value);
      if (value === null || value === undefined || value === '') value = '-';
      return {
        text: String(value),
        fontSize: finalFontSize,
        margin: [1, 1, 1, 1],
      };
    })
  );

  const docDefinition = {
    pageSize,
    pageOrientation: 'landscape',
    pageMargins: [10, 40, 10, 20],
    header: (currentPage, pageCount) => ({
      columns: [
        {
          stack: [
            { text: 'Inventario Disponible para Venta', style: 'title', color: '#1F2937' },
            { text: `Generado: ${formatDateTime(new Date())}`, fontSize: 8, color: '#6B7280', margin: [0, 2, 0, 0] },
          ],
          margin: [15, 15, 0, 0],
        },
        {
          text: `Página ${currentPage} de ${pageCount}`,
          alignment: 'right',
          fontSize: 8,
          color: '#6B7280',
          margin: [0, 20, 15, 0],
        },
      ],
    }),
    content: [
      buildTotalBar(`Total de vehículos: ${rows.length}`, 12),
      { text: 'Detalle de vehículos:', style: 'subheader', margin: [0, 4, 0, 8] },
      {
        table: {
          headerRows: 1,
          widths: visibleColumns.map(getSafeColumnWidth),
          body: [headerRow, ...bodyRows],
        },
        layout: {
          hLineWidth: () => 0.5,
          vLineWidth: () => 0.5,
          hLineColor: () => '#E5E7EB',
          vLineColor: () => '#E5E7EB',
          fillColor: (rowIndex) => (rowIndex % 2 === 0 ? null : '#F9FAFB'),
        },
      },
    ],
    styles: pdfStylesBase,
    defaultStyle: pdfDefaultStyle,
  };

  const filename = buildFilename({ sitio: 'inventario_ventas', subSitio: 'detalle' });
  pdfMake.createPdf(docDefinition).download(`${filename}.pdf`);
}
